import { createHash } from 'crypto';

export const fundraisersFragment = /* GraphQL */ `
  fragment FundraiserCharity on Charity {
    legacyId
    displayName
  }

  fragment Fundraisers on PageLeaderboardConnection {
    nodes {
      legacyId
      url
      status
      owner {
        legacyId
        avatar
        name
      }
      targetWithCurrency {
        value
        currencyCode
      }
      donationSummary {
        totalAmount {
          value
          currencyCode
        }
      }
      relationships {
        beneficiaries(first: 1) {
          nodes {
            __typename
            ...FundraiserCharity
          }
        }
      }
    }
    pageInfo {
      hasNextPage
      endCursor
    }
    totalCount
  }
`;

export const pageDataQuery = /* GraphQL */ `
  ${fundraisersFragment}

  fragment PageCoverImage on ImageMedia {
    url
    alt
  }

  fragment PageCoverVideo on VideoMedia {
    embeddedVideoURL
  }

  fragment PageCoverLivestream on LivestreamMedia {
    embeddedStreamURL
    embeddedStreamChatURL
  }

  fragment PageGalleryImage on ImageMedia {
    id
    url
  }

  query getPageData($slug: Slug, $id: ID) {
    page(type: ONE_PAGE, slug: $slug, id: $id) {
      legacyId
      slug
      type
      title
      logo
      status
      endDate
      url
      status
      product {
        ownerType
        name
        allowedPointsOfView
      }
      owner {
        id
        legacyId
        name
        avatar
      }
      cover {
        __typename
        ...PageCoverImage
        ...PageCoverVideo
        ...PageCoverLivestream
      }
      gallery(first: 100) {
        nodes {
          __typename
          ...PageGalleryImage
        }
      }
      storyWithType {
        type
        content
      }
      summary
      targetWithCurrency {
        currencyCode
        value
      }
      activity {
        name
        date
      }
      occasion {
        type
        date
        names
        description
      }
      donationSummary {
        donationCount
        aggregatedDonationsCount
        onlineDonationCount
        totalAmount {
          value
          currencyCode
        }
        totalMatched {
          type
          amount {
            value
            currencyCode
          }
        }
        onlineAmount {
          value
          currencyCode
        }
        offlineAmount {
          value
          currencyCode
        }
        directDonationAmount {
          value
          currencyCode
        }
        fundraiserRaisedAmount {
          value
          currencyCode
        }
        textDonations {
          value
          currencyCode
        }
      }
      branding {
        primaryColor
        backgroundColor
        backgroundImage
        headerImage
        externalFooterBackgroundColor
        externalFooterForegroundColor
        externalHeaderBackgroundColor
        externalHeaderForegroundColor
      }
      reason {
        icon
        reason
        reasonUrl
      }
      donations(first: 5) {
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          legacyId
          creationDate
          amount {
            value
            currencyCode
          }
          giftAidAmount {
            value
            currencyCode
          }
          displayName
          avatar
          message
        }
      }
      relationships {
        config {
          membersDisabled
        }
        beneficiaries(first: 6) {
          nodes {
            __typename
            ... on Charity {
              id
              legacyId
              externalId
              displayName
              description
              logo
              registrationNumber
              profilePageUrl
              countryCode
              impactStatement
              slug
            }
          }
          totalCount
        }
        teams: parents(productName: TEAM) {
          page {
            legacyId
            title
            url
            status
          }
        }
        events: parents(productName: EVENT) {
          requiredForChildren
          page {
            legacyId
            externalId
            launchDate
            endDate
            title
          }
        }
        campaigns: parents(productName: CAMPAIGN) {
          requiredForChildren
          page {
            legacyId
            title
            url
            summary
            logo
            relationships {
              beneficiaries(first: 1) {
                nodes {
                  ... on Charity {
                    __typename
                    registrationNumber
                    displayName
                    profilePageUrl
                  }
                }
              }
            }
          }
        }
        parents {
          # Needed by data generator to build parent lookup items
          page {
            legacyId
          }
        }
      }
      occasion {
        type
        date
        names
        description
      }
      totals {
        timeline {
          measurementDomain
          amounts {
            unit
            value
          }
        }
      }
      stravaSettings: application(name: "strava", type: FITNESS) {
        ... on FitnessApplication {
          settings {
            measurementSystem
            showTotaliser
            subscribedActivities
          }
        }
      }
      fitbitSettings: application(name: "fitbit", type: FITNESS) {
        ... on FitnessApplication {
          settings {
            measurementSystem
            showTotaliser
            subscribedActivities
          }
        }
      }
      membershipPolicy
      fundraisers: leaderboard(type: FUNDRAISERS, first: 5) {
        ...Fundraisers
      }
    }
  }
`;

export function getQueryHash(): string {
  const hash = createHash('sha1');
  hash.update(pageDataQuery);
  return hash.digest('hex');
}
